// Feestdag-naar-rato compensatie (parttimers) — pure berekening.
//
// Achtergrond: een parttimer heeft wettelijk naar rato recht op evenveel feestdagen
// als een fulltimer (art. 7:648 BW; College voor de Rechten van de Mens 2004-151).
// Valt een doordeweekse feestdag op de vaste vrije dag van de parttimer, dan loopt
// hij die mis terwijl een fulltimer hem wél betaald vrij krijgt. We compenseren dat
// verschil in extra bovenwettelijk verlof.
//
// Formule (PO-keuze "pro-rata pariteit, netto"):
//   recht        = #doordeweekse-feestdagen × (contracturen / 5)
//   reedsGenoten = som van de roosteruren op de feestdagen die op een WERKdag vallen
//   compensatie  = max(0, recht − reedsGenoten)
//
// Een fulltimer (alle ma-vr-feestdagen op een werkdag) komt netto op 0 uit; een
// parttimer houdt zijn pro-rata tekort over. Eén bron per gegeven: de feestdag-regel
// komt uit `isFeestdag` (Goede Vrijdag niet, Bevrijdingsdag enkel in lustrumjaren) en
// de daguren uit `contractUrenVoorWeekdag` — exact dezelfde resolvers als de
// capaciteit-engine en de weekstaat-afwezigheid-loaders.
//
// Puur (geen DB, geen Prisma) zodat de regel deterministisch te testen is; de caller
// (apps/api) levert de PublicHoliday-records + het weekrooster aan.

import { contractUrenVoorWeekdag, type WeekRooster } from './contract-rooster'
import { isFeestdag, type PublicHolidayInfo } from './leave-calculations'

export interface FeestdagCompensatieInput {
  /** Contracturen per week (contractHoursPerWeek). /5 = fulltime-equivalente dag. */
  contracturen: number
  /** Vast weekrooster (uren per dag); 0 op een vaste vrije dag of weekend. */
  weekRooster: WeekRooster
  /** Alle PublicHoliday-records van het kalenderjaar (date + isVerplicht + isLustrumOnly). */
  feestdagen: PublicHolidayInfo[]
}

export interface FeestdagCompensatieUitkomst {
  /** Compensatie in uren (>= 0, 2 decimalen) — toe te kennen als bovenwettelijk verlof. */
  uren: number
  /** Aantal feestdagen dat op een doordeweekse dag (ma-vr) viel. */
  doordeweekseFeestdagen: number
  /** Daarvan: hoeveel op de vaste vrije dag van de medewerker (0-urendag). */
  feestdagenOpVrijeDag: number
  /** Het berekende recht (#doordeweekse-feestdagen × contracturen/5), 2 decimalen. */
  recht: number
  /** De reeds genoten roosteruren op werkdag-feestdagen, 2 decimalen. */
  reedsGenoten: number
}

function round2(n: number): number {
  // PO-besluit 2026-06-01: exact 2 decimalen (geen kwartieren), Decimal(8,2)-precisie.
  return Math.round(n * 100) / 100
}

/** JS getUTCDay: 1..5 = ma..vr. Onze feestdag-datums zijn UTC-middernacht van de NL-dag. */
function isDoordeweeks(datum: Date): boolean {
  const dow = datum.getUTCDay()
  return dow >= 1 && dow <= 5
}

/**
 * Bereken de feestdag-naar-rato compensatie (uren) voor één medewerker over de
 * meegegeven feestdagen (één kalenderjaar). Zie de module-toelichting voor de
 * formule. Verwacht een ingevuld `weekRooster` (de caller kent alleen de vaste vrije
 * dag bij `werktVastRooster`); zonder rooster is de vrije dag onbekend en hoort er
 * niet gecompenseerd te worden.
 */
export function berekenFeestdagCompensatie(
  input: FeestdagCompensatieInput,
): FeestdagCompensatieUitkomst {
  const { contracturen, weekRooster, feestdagen } = input

  let doordeweekseFeestdagen = 0
  let feestdagenOpVrijeDag = 0
  let reedsGenoten = 0

  for (const f of feestdagen) {
    // Alleen echte vrije feestdagen (Goede Vrijdag niet; Bevrijdingsdag enkel in
    // lustrumjaren) die op een doordeweekse dag vallen. Een weekend-feestdag krijgt
    // een fulltimer ook niet vrij, dus telt niet mee (Koningsdag die naar zaterdag
    // schuift valt hier vanzelf weg).
    if (!isFeestdag(f.date, feestdagen, true)) continue
    if (!isDoordeweeks(f.date)) continue

    doordeweekseFeestdagen++
    const roosterUren = contractUrenVoorWeekdag(weekRooster, f.date)
    if (roosterUren > 0) {
      // Feestdag op een werkdag: al genoten (betaalde vrije dag).
      reedsGenoten += roosterUren
    } else {
      // Feestdag op de vaste vrije dag: misgelopen.
      feestdagenOpVrijeDag++
    }
  }

  const recht = contracturen > 0 ? doordeweekseFeestdagen * (contracturen / 5) : 0
  const uren = Math.max(0, round2(recht - reedsGenoten))

  return {
    uren,
    doordeweekseFeestdagen,
    feestdagenOpVrijeDag,
    recht: round2(recht),
    reedsGenoten: round2(reedsGenoten),
  }
}
